import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Sparkles, X, Play, Loader2 } from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';

export default function DragandDrop() {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [upscaledImage, setUpscaledImage] = useState(null);
    const { user } = useAuth();

    const onDrop = useCallback((acceptedFiles) => {
        const selected = acceptedFiles && acceptedFiles[0];
        if (selected) {
            setFile(selected);
            setPreview(URL.createObjectURL(selected));
            setUpscaledImage(null);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [], 'video/*': [] },
        maxFiles: 1,
    });

    const handleCancel = () => {
        setFile(null);
        setPreview(null);
        setUpscaledImage(null);
    };

    const handleStart = async () => {
        if (!file) return;

        setIsLoading(true);

        try {
            // 1. Get presigned URL for original image
            if (!user?.email) {
                console.error("User email not available.");
                setIsLoading(false);
                return;
            }

            const signedUrlResponse = await fetch('/api/create-signedurl', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename: file.name, email: user.email }),
            });
            const { uploadUrl, downloadUrl: signedDownloadUrl } = await signedUrlResponse.json();


            // 2. Upload original image
            await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });

            // 3. Call runpod worker
            const runpodResponse = await fetch('/api/call-runpod-worker', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    presigned_url: signedDownloadUrl,
                    email: user.email,
                    filename: file.name,
                }),
            });
            const { upscaled_url } = await runpodResponse.json();

            setUpscaledImage(upscaled_url);

        } catch (error) {
            console.error("Error during upscaling process:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='w-full flex flex-col items-center justify-center'>
            <AnimatePresence mode="wait">
                {!file ? (
                    <motion.div
                        key="dropzone"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        {...getRootProps({
                            className: `relative border-2 border-dashed rounded-2xl p-12 cursor-pointer transition-all max-w-2xl w-full group ${
                                isDragActive
                                    ? 'border-fuchsia-500 bg-fuchsia-500/10 shadow-[0_0_30px_rgba(255,0,255,0.5)] scale-105'
                                    : 'border-fuchsia-500/40 bg-black/20 hover:border-fuchsia-500/70 hover:bg-fuchsia-500/5'
                            }`
                        })}
                    >
                        <input {...getInputProps()} />
                        <div className="flex flex-col items-center justify-center gap-6">
                            <motion.div
                                animate={isDragActive ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] } : {}}
                                transition={{ duration: 0.5, repeat: isDragActive ? Infinity : 0 }}
                            >
                                <Upload 
                                    size={64} 
                                    className={`transition-colors ${
                                        isDragActive ? 'text-fuchsia-400' : 'text-fuchsia-500/60 group-hover:text-fuchsia-400'
                                    }`}
                                />
                            </motion.div>
                            {isDragActive ? (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className='font-rocket text-center text-2xl text-fuchsia-400 drop-shadow-[0_0_10px_rgba(255,0,255,0.8)]'
                                >
                                    Drop the file here...
                                </motion.p>
                            ) : (
                                <>
                                    <p className='font-rocket text-center text-xl text-white/80 group-hover:text-white transition-colors'>
                                        Drop an image or video here to start
                                    </p>
                                    <p className='text-sm text-gray-400 group-hover:text-gray-300 transition-colors'>
                                        or click to browse
                                    </p>
                                </>
                            )}
                            <div className="flex gap-2 mt-4">
                                {['image', 'video'].map((type, i) => (
                                    <span
                                        key={type}
                                        className="px-3 py-1 text-xs bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-full text-fuchsia-300"
                                    >
                                        {type}
                                    </span>
                                ))}
                            </div>
                        </div>
                        {/* Animated border glow */}
                        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-fuchsia-500/0 via-fuchsia-500/50 to-fuchsia-500/0 blur-xl animate-pulse" />
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="preview"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className='relative bg-gradient-to-br from-black/80 via-purple-900/40 to-black/80 backdrop-blur-md border-2 border-fuchsia-500/40 rounded-2xl p-6 flex flex-col items-center gap-6 max-w-3xl w-full shadow-[0_0_40px_rgba(255,0,255,0.3)]'
                    >
                        {/* Close button */}
                        <button
                            onClick={handleCancel}
                            className="absolute top-4 right-4 p-2 rounded-full bg-black/60 hover:bg-red-500/20 border border-red-500/40 hover:border-red-500 transition-all group"
                        >
                            <X size={20} className="text-red-400 group-hover:text-red-300" />
                        </button>

                        {/* Preview content */}
                        <div className="relative w-full flex justify-center">
                            {upscaledImage ? (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="relative"
                                >
                                    <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-purple-600 rounded-2xl blur opacity-75 animate-pulse" />
                                    <img 
                                        src={upscaledImage} 
                                        alt="Upscaled" 
                                        className='relative rounded-2xl max-h-[500px] object-contain shadow-2xl' 
                                    />
                                    <div className="absolute top-2 left-2 px-3 py-1 bg-green-500/80 backdrop-blur-sm rounded-full flex items-center gap-2">
                                        <Sparkles size={16} className="text-white" />
                                        <span className="text-white text-sm font-semibold">Upscaled</span>
                                    </div>
                                </motion.div>
                            ) : file.type.startsWith('image/') ? (
                                <motion.img 
                                    src={preview} 
                                    alt="Preview" 
                                    className='rounded-2xl max-h-[500px] object-contain shadow-xl border border-fuchsia-500/30' 
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                />
                            ) : file.type.startsWith('video/') ? (
                                <motion.video 
                                    src={preview} 
                                    controls 
                                    className='rounded-2xl max-h-[500px] object-contain shadow-xl border border-fuchsia-500/30' 
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                />
                            ) : null}
                        </div>

                        {/* Action buttons */}
                        <div className='flex items-center gap-4 w-full justify-center'>
                            {!isLoading && !upscaledImage && (
                                <motion.button
                                    onClick={handleStart}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className='px-8 py-3 bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white rounded-lg font-semibold flex items-center gap-2 shadow-[0_0_20px_rgba(255,0,255,0.5)] hover:shadow-[0_0_30px_rgba(255,0,255,0.7)] transition-all'
                                >
                                    <Play size={20} />
                                    Start Upscaling
                                </motion.button>
                            )}
                            {isLoading && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex flex-col items-center gap-3"
                                >
                                    <Loader2 className="text-fuchsia-400 animate-spin" size={32} />
                                    <p className="text-white font-semibold">Upscaling in progress...</p>
                                    <p className="text-gray-400 text-sm">This may take a few moments</p>
                                </motion.div>
                            )}
                            {!isLoading && (
                                <motion.button
                                    onClick={handleCancel}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className='px-6 py-3 bg-gray-800/60 border border-gray-600 text-white rounded-lg hover:bg-gray-700/60 hover:border-gray-500 transition-all'
                                >
                                    Cancel
                                </motion.button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}